import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { DirectiveModule } from '../../../../core/directives';
import { LoadingService } from '../../../../core/services/loading.service';
import { TagService } from '../../../../core/api/tag.service';
import { HelpersService } from '../../../../core/services/helpers.service';
import { FileService } from '../../../../core/api/file.service';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { FilePreviewComponent } from '../../../../components/file-preview/file-preview.component';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-action-settlement',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    DirectiveModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './action-settlement.component.html',
  styleUrl: './action-settlement.component.scss',
})
export class ActionSettlementComponent {
  form: FormGroup;
  tag: any;
  uploadStatus: boolean = false;
  fileExists: boolean = false;
  fileUrl: any;
  file: any;
  fileName: string = '';
  uploadedFiles: any[] = [];
  showDetail: boolean = false;
  isEdit: boolean = false;
  isDownloading = false;

  constructor(
    public dialogRef: MatDialogRef<ActionSettlementComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loadingService: LoadingService,
    private fb: FormBuilder,
    private tagService: TagService,
    private helpers: HelpersService,
    private fileService: FileService,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      amount: [null, [Validators.required]],
      tagId: [null, [Validators.required]],
      desciption: [null, [Validators.required]],
      notes: [null, [Validators.required]],
      location: [null, [Validators.required]],
      fileId: [null, [Validators.required]],
      latitude: [0, [Validators.required]],
      longitude: [0, [Validators.required]],
      currency: ['Rp', [Validators.required]],
      settlementDate: [null, [Validators.required]],
    });
    if (data.settlementData) {
      const payload = { ...data.settlementData };
      if (payload['description']) {
        payload['desciption'] = payload['description'];
      }
      payload['settlementDate'] = this.toDateInputValue(
        payload['settlementDate'] ??
          payload['created_at'] ??
          payload['updated_at'],
      );
      this.initializeSettlementFiles(payload);
      this.showDetail = true;
      payload['fileId'] = this.file?.id;
      this.form.patchValue(payload);
    }
    if (data.isEdit) {
      this.showDetail = false;
      this.isEdit = data.isEdit;
    }
    this.getTag();
  }

  getTag() {
    this.loadingService.startLoading();
    this.tagService.getAllData().then(
      (res) => {
        this.loadingService.endLoading();
        this.tag = res;
      },
      (error) => {
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  submit() {
    if (this.form.valid) {
      const data = this.form.value;
      data['amount'] = this.helpers.parseAmount(data['amount']);
      data['fileIds'] = this.uploadedFiles.map((item: any) => item.id);
      data['fileId'] = this.getPrimaryFile()?.id;
      data['file'] = this.getPrimaryFile();
      this.dialogRef.close(data);
    } else {
      this.helpers.sbError('Lengkapi form terlebih dahulu!');
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }

  getDialogTitle(): string {
    if (this.isEdit) return 'Edit settlement';
    if (this.showDetail) return 'Detail settlement';
    return 'Add settlement';
  }

  get isDetailView(): boolean {
    return this.showDetail && !this.isEdit;
  }

  getDisplayFileName(): string {
    const file = this.getPrimaryFile();
    return file?.fileName ?? 'Attachment';
  }

  getTagName(): string {
    return (
      this.data?.settlementData?.tag?.name ??
      this.tag?.find((item: any) => item.id === this.form.get('tagId')?.value)
        ?.name ??
      '-'
    );
  }

  getFormattedAmount(): string {
    const currency = this.form.get('currency')?.value ?? 'Rp';
    const amount = this.form.get('amount')?.value;
    if (amount == null || amount === '') return '-';
    const parsed = this.helpers.parseAmount(amount);
    return `${currency} ${Number(parsed).toLocaleString('id-ID')}`;
  }

  getFormattedDate(): string {
    const raw =
      this.data?.settlementData?.settlementDate ??
      this.data?.settlementData?.created_at ??
      this.form.get('settlementDate')?.value;
    if (!raw) return '-';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  formatFileSize(size?: number): string {
    if (!size) return '';
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  }

  getFileIconClass(): string {
    const file = this.getPrimaryFile();
    const type = (file?.fileType || '').toLowerCase();
    const name = (file?.fileName || '').toLowerCase();

    if (type.includes('pdf') || name.endsWith('.pdf')) {
      return 'fa-file-pdf';
    }
    if (type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/.test(name)) {
      return 'fa-file-image';
    }
    return 'fa-file';
  }

  hasFieldError(name: string): boolean {
    const control = this.form.get(name);
    return !!(control?.invalid && control?.touched);
  }

  private toDateInputValue(value: unknown): string | null {
    if (!value) return null;
    const date = new Date(value as string | number | Date);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  uploadFile(event: Event) {
    this.uploadStatus = true;
    const target = event.target as HTMLInputElement | null;
    const fileList = target?.files;
    if (fileList?.length) {
      const files = Array.from(fileList);
      const uploads = files.map(
        (file) =>
          new Promise<any>((resolve, reject) => {
            try {
              this.fileService.upload(file).subscribe({
                next: (res: any) => {
                  if (
                    res?.type === HttpEventType.Response &&
                    res?.body?.data
                  ) {
                    resolve(res.body.data);
                  }
                },
                error: (err: any) => reject(err),
              });
            } catch (err: any) {
              reject(err);
            }
          })
      );
      this.loadingService.startLoading();
      Promise.all(uploads)
        .then((results: any[]) => {
          const uploaded = results.filter(Boolean);
          if (!uploaded.length) return;
          this.uploadedFiles = [...this.uploadedFiles, ...uploaded];
          this.file = this.getPrimaryFile();
          this.form.get('fileId')?.setValue(this.file?.id ?? null);
          this.fileExists = !!this.file;
          this.fileName =
            this.uploadedFiles.length === 1
              ? this.uploadedFiles[0].fileName
              : `${this.uploadedFiles.length} files selected`;
        })
        .catch((err: any) => {
          this.helpers.sbError(err.message);
        })
        .finally(() => {
          this.loadingService.endLoading();
          this.uploadStatus = false;
          if (target) target.value = '';
        });
    } else {
      this.helpers.sbError('Tidak ada file yang akan diupload!');
      this.uploadStatus = false;
      if (target) target.value = '';
    }
  }

  private initializeSettlementFiles(payload: any): void {
    const fileFromApi = payload.file ?? null;

    if (fileFromApi?.id) {
      this.uploadedFiles = [fileFromApi];
    } else if (payload.fileIds?.length) {
      this.uploadedFiles = payload.fileIds.map((id: number) => ({
        id,
        fileName: `File #${id}`,
      }));
    } else {
      this.uploadedFiles = [];
    }

    this.file = this.getPrimaryFile();
    this.fileExists = !!this.file;
    this.fileName = this.getDisplayFileName();
  }

  getPrimaryFile(): any {
    if (!this.uploadedFiles.length) {
      return this.file ?? this.data?.settlementData?.file ?? null;
    }
    return this.uploadedFiles[this.uploadedFiles.length - 1];
  }

  download() {
    if (this.isDownloading) return;

    const file = this.getPrimaryFile();
    if (file) {
      const isMobile = window.innerWidth < 768;
      const canPreviewInline = this.helpers.canPreviewInline(file);

      if (isMobile || !canPreviewInline) {
        this.openFileInNewTab(file);
        return;
      }

      this.dialog
        .open(FilePreviewComponent, {
          width: '70vw',
          maxWidth: '70vw',
          maxHeight: '95vh',
          data: { file },
        })
        .afterClosed()
        .subscribe((item) => {
          if (item) {
          }
        });
    } else {
      this.helpers.sbError('Tidak ada file yang bisa diunduh');
    }
  }

  private openFileInNewTab(file: any) {
    this.isDownloading = true;
    this.loadingService.startLoading();
    this.fileService.download(file?.id).subscribe(
      (res) => {
        this.isDownloading = false;
        this.loadingService.endLoading();
        this.helpers.openBlobInNewTab(res, file);
      },
      (error) => {
        this.isDownloading = false;
        this.loadingService.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }
}
